import { Components, Theme, ThemeOptions } from '@mui/material';
import { CSSProperties } from '@mui/styles';

import { mdPx, smPx } from './breakpoints';
import { primaryColors } from './styles/colors';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    display1: CSSProperties;
    lead: CSSProperties;
    base: CSSProperties;
    small: CSSProperties;
    xsmall: CSSProperties;
    body3: CSSProperties;
    label1: CSSProperties;
    label2: CSSProperties;
    label3: CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    display1?: CSSProperties;
    lead?: CSSProperties;
    base?: CSSProperties;
    small?: CSSProperties;
    xsmall?: CSSProperties;
    body3?: CSSProperties;
    label1?: CSSProperties;
    label2?: CSSProperties;
    label3?: CSSProperties;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display1: true;
    lead: true;
    base: true;
    small: true;
    xsmall: true;
    body3: true;
    label1: true;
    label2: true;
    label3: true;
  }
}

const margin = 0;

export const display1 = {
  fontFamily: 'geom-graphic',
  margin,
  fontSize: '57.33px',
  lineHeight: '64px',
  letterSpacing: '',
  color: primaryColors[50],
  [`@media (min-width:${smPx})`]: {
    fontSize: '76.29px',
    lineHeight: '88px',
    letterSpacing: '',
  },
  [`@media (min-width:${mdPx})`]: {
    fontSize: '119.66px',
    lineHeight: '140px',
    letterSpacing: '-.05em',
  },
};
export const h1 = {
  fontFamily: 'geom-graphic',
  margin,
  fontSize: '47.78px',
  lineHeight: '56px',
  letterSpacing: '',
  fontWeight: 300,
  color: primaryColors[50],
  [`@media (min-width:${smPx})`]: {
    fontSize: '61.04px',
    lineHeight: '72px',
    letterSpacing: '',
  },
  [`@media (min-width:${mdPx})`]: {
    fontSize: '89.76px',
    lineHeight: '120px',
    letterSpacing: '-.04em',
  },
};
export const h2 = {
  fontFamily: 'geom-graphic',
  margin,
  fontSize: '39.81px',
  lineHeight: '48px',
  letterSpacing: '',
  fontWeight: 300,
  color: primaryColors[50],
  [`@media (min-width:${smPx})`]: {
    fontSize: '48.83px',
    lineHeight: '56px',
    letterSpacing: '',
  },
  [`@media (min-width:${mdPx})`]: {
    fontSize: '67.34px',
    lineHeight: '80px',
    letterSpacing: '-.03em',
  },
};
export const h3 = {
  fontFamily: 'geom-graphic',
  margin,
  fontSize: '33.18px',
  lineHeight: '40px',
  letterSpacing: '',
  fontWeight: 300,
  color: primaryColors[50],
  [`@media (min-width:${smPx})`]: {
    fontSize: '39.06px',
    lineHeight: '48px',
    letterSpacing: '',
  },
  [`@media (min-width:${mdPx})`]: {
    fontSize: '50.52px',
    lineHeight: '64px',
    letterSpacing: '-.02em',
  },
};
export const h4 = {
  fontFamily: 'geom-graphic',
  margin,
  fontSize: '27.65px',
  lineHeight: '32px',
  letterSpacing: '',
  fontWeight: 300,
  color: primaryColors[50],
  [`@media (min-width:${smPx})`]: {
    fontSize: '31.25px',
    lineHeight: '36px',
    letterSpacing: '',
  },
  [`@media (min-width:${mdPx})`]: {
    fontSize: '37.90px',
    lineHeight: '48px',
    letterSpacing: '-.01em',
  },
};
export const h5 = {
  fontFamily: 'geom-graphic',
  margin,
  fontSize: '23.04px',
  lineHeight: '28px',
  letterSpacing: '',
  fontWeight: 300,
  color: primaryColors[50],
  [`@media (min-width:${smPx})`]: {
    fontSize: '25px',
    lineHeight: '28px',
    letterSpacing: '',
  },
  [`@media (min-width:${mdPx})`]: {
    fontSize: '28.43px',
    lineHeight: '36px',
    letterSpacing: '.01em',
  },
};
export const h6 = {
  fontFamily: 'geom-graphic',
  margin,
  fontSize: '19.20px',
  lineHeight: '24px',
  letterSpacing: '',
  fontWeight: 300,
  color: primaryColors[50],
  [`@media (min-width:${smPx})`]: {
    fontSize: '20px',
    lineHeight: '24px',
    letterSpacing: '',
  },
  [`@media (min-width:${mdPx})`]: {
    fontSize: '21.33px',
    lineHeight: '32px',
    letterSpacing: '.02em',
  },
};
export const lead = {
  fontFamily: 'geom-graphic',
  margin,
  fontSize: '19.20px',
  lineHeight: '24px',
  letterSpacing: '',
  fontWeight: 300,
  color: primaryColors[50],
  [`@media (min-width:${smPx})`]: {
    fontSize: '20px',
    lineHeight: '24px',
    letterSpacing: '',
  },
  [`@media (min-width:${mdPx})`]: {
    fontSize: '21.33px',
    lineHeight: '32px',
    letterSpacing: '.04em',
  },
};
export const base = {
  fontFamily: 'geom-graphic',
  margin,
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '',
  fontWeight: 300,
  [`@media (min-width:${smPx})`]: {
    fontSize: '16px',
    lineHeight: '20px',
    letterSpacing: '',
  },
  [`@media (min-width:${mdPx})`]: {
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '.05em',
  },
};
export const small = {
  fontFamily: 'geom-graphic',
  margin,
  fontSize: '13.33px',
  lineHeight: '16px',
  letterSpacing: '',
  color: primaryColors[50],
  [`@media (min-width:${smPx})`]: {
    fontSize: '12.8px',
    lineHeight: '16px',
    letterSpacing: '',
  },
  [`@media (min-width:${mdPx})`]: {
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '.07em',
  },
};
export const xsmall = {
  fontFamily: 'geom-graphic',
  margin,
  fontSize: '11.11px',
  lineHeight: '12px',
  letterSpacing: '',
  color: primaryColors[50],
  [`@media (min-width:${smPx})`]: {
    fontSize: '10.24px',
    lineHeight: '12px',
    letterSpacing: '',
  },
  [`@media (min-width:${mdPx})`]: {
    fontSize: '9px',
    lineHeight: '12px',
    letterSpacing: '.08em',
  },
};

export const body1 = {
  fontFamily: 'Space Grotesk',
  margin,
  fontSize: '21.33px',
  lineHeight: '32px',
  letterSpacing: '0',
  color: primaryColors[100],
};
export const body2 = {
  fontFamily: 'Space Grotesk',
  margin,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0',
  color: primaryColors[100],
};
export const body3 = {
  fontFamily: 'Space Grotesk',
  margin,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '.05em',
  fontWeight: 500,
  color: primaryColors[100],
};
export const label1 = {
  fontFamily: 'Space Grotesk',
  margin,
  fontSize: '21.33px',
  lineHeight: '32px',
  letterSpacing: '.15em',
  fontWeight: 500,
  color: primaryColors[100],
};
export const label2 = {
  fontFamily: 'Space Grotesk',
  margin,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '.15em',
  fontWeight: 500,
  color: primaryColors[100],
};

export const label3 = {
  fontFamily: 'Space Grotesk',
  margin,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '1.25px',
  fontWeight: 500,
  color: primaryColors[100],
};

export const typography: ThemeOptions['typography'] = {
  display1,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  lead,
  base,
  small,
  xsmall,
  body1,
  body2,
  body3,
  label1,
  label2,
  label3,
};

export const MuiTypography: Components<Omit<Theme, 'components'>>['MuiTypography'] = {
  defaultProps: {
    variantMapping: {
      display1: 'h1',
      lead: 'p',
      base: 'p',
      small: 'p',
      xsmall: 'p',
      body3: 'p',
      label1: 'p',
      label2: 'p',
      label3: 'p',
    },
  },
};

export const getGradientTypographySx = (gradient: string) => ({
  background: gradient,
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
});
